import React, { useState } from 'react';
import styled from 'styled-components';
import yaml from 'js-yaml';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 8px;
  width: 800px;
  max-width: 90%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Header = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e1e4e8;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  color: #24292f;
`;

const Content = styled.div`
  padding: 20px;
  overflow-y: auto;
  flex: 1;
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #24292f;
  margin-bottom: 8px;
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #0969da;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #0969da;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  font-size: 13px;
  font-family: 'Monaco', 'Menlo', monospace;
  resize: vertical;
  min-height: 100px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #0969da;
  }
`;

const ResponseBox = styled.div`
  padding: 16px;
  background: #f6f8fa;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  font-size: 13px;
  font-family: 'Monaco', 'Menlo', monospace;
  white-space: pre-wrap;
  max-height: 300px;
  overflow-y: auto;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.$status < 300 ? '#28a745' : props.$status < 400 ? '#fb8500' : '#cf222e'};
  color: white;
  margin-right: 8px;
`;

const Footer = styled.div`
  padding: 16px 20px;
  border-top: 1px solid #e1e4e8;
  display: flex;
  justify-content: space-between;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid ${props => props.$primary ? '#0969da' : '#d0d7de'};
  background: ${props => props.$primary ? '#0969da' : 'white'};
  color: ${props => props.$primary ? 'white' : '#24292f'};
  
  &:hover {
    background: ${props => props.$primary ? '#0860ca' : '#f6f8fa'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

function TestEndpointModal({ spec, onClose }) {
  const [endpoint, setEndpoint] = useState('');
  const [method, setMethod] = useState('GET');
  const [url, setUrl] = useState('');
  const [headers, setHeaders] = useState('');
  const [body, setBody] = useState('');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);

  const endpoints = [];
  try {
    const parsed = yaml.load(spec);
    const baseUrl = parsed.servers?.[0]?.url || '';
    for (const path in parsed.paths || {}) {
      for (const m in parsed.paths[path]) {
        if (['get', 'post', 'put', 'delete', 'patch'].includes(m)) {
          endpoints.push({
            path,
            method: m.toUpperCase(),
            summary: parsed.paths[path][m].summary || `${m.toUpperCase()} ${path}`,
            url: baseUrl + path
          });
        }
      }
    }
  } catch (e) {}

  const handleEndpointChange = (value) => {
    setEndpoint(value);
    const ep = endpoints.find(e => `${e.method} ${e.path}` === value);
    if (ep) {
      setMethod(ep.method);
      setUrl(ep.url);
    }
  };

  const handleTest = async () => {
    setLoading(true);
    setResponse(null);
    
    try {
      const options = {
        method,
        headers: headers ? JSON.parse(headers) : {}
      };
      
      if (['POST', 'PUT', 'PATCH'].includes(method) && body) {
        options.body = body;
        options.headers['Content-Type'] = 'application/json';
      }

      const startTime = Date.now();
      const res = await fetch(url, options);
      const duration = Date.now() - startTime;
      const responseText = await res.text();
      
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch {
        responseData = responseText;
      }

      setResponse({
        status: res.status,
        statusText: res.statusText,
        duration,
        headers: Object.fromEntries(res.headers.entries()),
        data: responseData
      });
    } catch (err) {
      setResponse({
        error: err.message
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>🧪 Test API Endpoint</Title>
        </Header>
        <Content>
          <Section>
            <Label>Endpoint</Label>
            <Select value={endpoint} onChange={(e) => handleEndpointChange(e.target.value)}>
              <option value="">Select an endpoint...</option>
              {endpoints.map(ep => (
                <option key={`${ep.method} ${ep.path}`} value={`${ep.method} ${ep.path}`}>
                  {ep.method} {ep.path} - {ep.summary}
                </option>
              ))}
            </Select>
          </Section>

          <Section>
            <Label>URL</Label>
            <Input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://api.example.com/endpoint"
            />
          </Section>

          <Section>
            <Label>Headers (JSON)</Label>
            <TextArea
              value={headers}
              onChange={(e) => setHeaders(e.target.value)}
              placeholder='{"Authorization": "Bearer token"}'
            />
          </Section>

          {['POST', 'PUT', 'PATCH'].includes(method) && (
            <Section>
              <Label>Request Body (JSON)</Label>
              <TextArea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder='{"key": "value"}'
              />
            </Section>
          )}

          {response && (
            <Section>
              <Label>Response</Label>
              <ResponseBox>
                {response.error ? (
                  <div style={{ color: '#cf222e' }}>Error: {response.error}</div>
                ) : (
                  <>
                    <div style={{ marginBottom: '12px' }}>
                      <StatusBadge $status={response.status}>{response.status}</StatusBadge>
                      {response.statusText} • {response.duration}ms
                    </div>
                    {JSON.stringify(response.data, null, 2)}
                  </>
                )}
              </ResponseBox>
            </Section>
          )}
        </Content>
        <Footer>
          <Button onClick={onClose}>Close</Button>
          <Button $primary onClick={handleTest} disabled={!url || loading}>
            {loading ? 'Testing...' : 'Send Request'}
          </Button>
        </Footer>
      </Modal>
    </Overlay>
  );
}

export default TestEndpointModal;
